import { collection, query, where, getDocs, updateDoc, doc, writeBatch } from 'firebase/firestore';
import { db } from '../../data/base';
import {
    FETCH_NOTIFICATIONS_REQUEST,
    FETCH_NOTIFICATIONS_SUCCESS,
    FETCH_NOTIFICATIONS_FAILURE,
    MARK_NOTIFICATION_AS_READ,
    MARK_ALL_NOTIFICATIONS_AS_READ,
    SET_NOTIFICATIONS_SUMMARY,
    UPDATE_NOTIFICATION_COUNTS,
    CLEAR_NOTIFICATIONS
} from './types';

// Action creators sincroni
export const fetchNotificationsRequest = () => ({
    type: FETCH_NOTIFICATIONS_REQUEST
});

export const fetchNotificationsSuccess = (notifications) => ({
    type: FETCH_NOTIFICATIONS_SUCCESS,
    payload: notifications
});

export const fetchNotificationsFailure = (error) => ({
    type: FETCH_NOTIFICATIONS_FAILURE,
    payload: error
});

export const markNotificationAsRead = (notificationId) => ({
    type: MARK_NOTIFICATION_AS_READ,
    payload: notificationId
});

export const markAllNotificationsAsRead = () => ({
    type: MARK_ALL_NOTIFICATIONS_AS_READ
});

export const setNotificationsSummary = (summary) => ({
    type: SET_NOTIFICATIONS_SUMMARY,
    payload: summary
});

export const updateNotificationCounts = (counts) => ({
    type: UPDATE_NOTIFICATION_COUNTS,
    payload: counts
});

export const clearNotifications = () => ({
    type: CLEAR_NOTIFICATIONS
});

// Thunk per recuperare le notifiche
export const fetchNotifications = (email) => async (dispatch) => {
    if (!email) return;

    dispatch(fetchNotificationsRequest());

    try {
        const notifsQuery = query(
            collection(db, "Notifications"),
            where("sentTo", "==", email)
        );

        const notifsSnapshot = await getDocs(notifsQuery);
        const notifsList = [];

        notifsSnapshot.forEach((doc) => {
            notifsList.push({
                id: doc.id,
                ...doc.data(),
                date: doc.data().date?.toDate()?.toISOString()
            });
        });

        // Ordina per data decrescente
        notifsList.sort((a, b) => {
            if (!a.date) return 1;
            if (!b.date) return -1;
            return new Date(b.date) - new Date(a.date);
        });

        dispatch(fetchNotificationsSuccess(notifsList));

        // Calcola e aggiorna i conteggi
        const counts = calculateNotificationCounts(notifsList);
        dispatch(updateNotificationCounts(counts));

        // Calcola il riassunto per tipo
        const summary = calculateNotificationsSummary(notifsList);
        dispatch(setNotificationsSummary(summary));

    } catch (error) {
        console.error("Error fetching notifications:", error);
        dispatch(fetchNotificationsFailure(error.message));
    }
};

// Thunk per segnare una notifica come letta
export const markNotificationAsReadThunk = (notificationId) => async (dispatch) => {
    try {
        await updateDoc(doc(db, "Notifications", notificationId), {
            read: true
        });

        dispatch(markNotificationAsRead(notificationId));
    } catch (error) {
        console.error("Error marking notification as read:", error);
        throw error;
    }
};

// Thunk per segnare tutte le notifiche come lette
export const markAllNotificationsAsReadThunk = (notifications) => async (dispatch) => {
    try {
        const batch = writeBatch(db);
        const unreadNotifications = notifications.filter(notification => !notification.read);

        unreadNotifications.forEach(notification => {
            const notificationRef = doc(db, "Notifications", notification.id);
            batch.update(notificationRef, { read: true });
        });

        await batch.commit();
        dispatch(markAllNotificationsAsRead());

        // Ricalcola i conteggi
        const updatedNotifications = notifications.map(n => ({ ...n, read: true }));
        const counts = calculateNotificationCounts(updatedNotifications);
        dispatch(updateNotificationCounts(counts));

        const summary = calculateNotificationsSummary(updatedNotifications);
        dispatch(setNotificationsSummary(summary));

    } catch (error) {
        console.error("Error marking all notifications as read:", error);
        throw error;
    }
};

// Funzioni di utilità
const calculateNotificationCounts = (notifications) => {
    const unreadNotifications = notifications.filter(notification => !notification.read);

    let frameValidationCount = 0;
    let ordersCount = 0;
    let totalUnread = unreadNotifications.length;

    unreadNotifications.forEach(notification => {
        const type = notification.type || "";
        if (type.includes("Variant")) {
            frameValidationCount++;
        } else if (type.includes("Order")) {
            ordersCount++;
        }
    });

    return {
        frameValidationCount,
        ordersCount,
        totalUnread
    };
};

const calculateNotificationsSummary = (notifications) => {
    const grouped = {};
    let totalUnread = 0;

    notifications.forEach(notification => {
        const type = notification.type || "Unknown";
        if (!grouped[type]) {
            grouped[type] = { total: 0, unread: 0 };
        }
        grouped[type].total++;
        if (!notification.read) {
            grouped[type].unread++;
            totalUnread++;
        }
    });

    return { grouped, totalUnread };
}; 